/**
 * Create/Edit Enterprise System Page
 *
 * Form to create a new enterprise system or edit an existing one.
 * Supports both create mode (/enterprise-systems/new) and edit mode (/enterprise-systems/:id/edit).
 *
 * Features:
 * - Autocomplete for system name (searches 30-system library)
 * - Autocomplete for vendor (searches vendor names)
 * - LLM-powered vendor suggestion when system name is entered
 * - Optionally triggers research after creation (create mode only)
 * - Pre-populates form with existing data in edit mode
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Server, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  createEnterpriseSystem,
  getEnterpriseSystem,
  updateEnterpriseSystem,
  searchSystemsLibrary,
  searchVendors,
  suggestVendor,
  type LibrarySystem,
} from '../lib/api/enterpriseSystemsApi';
import Autocomplete from '../components/ui/Autocomplete';

const CreateEnterpriseSystemPage: React.FC = () => {
  const navigate = useNavigate();
  const { systemId } = useParams<{ systemId: string }>();
  const isEditMode = !!systemId;

  // Form state
  const [formData, setFormData] = useState({
    system_name: '',
    vendor: '',
    category: 'ERP',
    custom_category_name: '',
    version: '',
    database_type: '',
    product_url: '',
    trigger_research: false,
  });

  // Autocomplete state
  const [isLoadingVendorSuggestion, setIsLoadingVendorSuggestion] = useState(false);

  // Fetch existing system data in edit mode
  const { data: existingSystemData, isLoading: isLoadingSystem } = useQuery({
    queryKey: ['enterprise-system', systemId],
    queryFn: () => getEnterpriseSystem(systemId!),
    enabled: isEditMode,
  });

  // Pre-populate form when editing
  useEffect(() => {
    if (isEditMode && existingSystemData?.system) {
      const system = existingSystemData.system;
      setFormData({
        system_name: system.system_name || '',
        vendor: system.vendor || '',
        category: system.category || 'ERP',
        custom_category_name: system.custom_category_name || '',
        version: system.version || '',
        database_type: system.database_type || '',
        product_url: system.product_url || '',
        trigger_research: false, // Don't trigger research on edit
      });
    }
  }, [isEditMode, existingSystemData]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createEnterpriseSystem,
    onSuccess: (data) => {
      // Show different message based on whether research was triggered
      if (data.research_triggered && data.research_job_id) {
        toast.success(
          `System created! 🎯 Research started (Job ID: ${data.research_job_id}). View progress in Research tab.`,
          { duration: 6000 }
        );
      } else {
        toast.success(`System created: ${data.system.system_name}`);
      }

      // Navigate to detail page
      navigate(`/enterprise-systems/${data.system.system_id}`);
    },
    onError: (error: any) => {
      toast.error(`Failed to create system: ${error.message}`);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: any) => updateEnterpriseSystem(systemId!, data),
    onSuccess: (data) => {
      toast.success(`System updated: ${data.system.system_name}`);
      // Navigate to detail page
      navigate(`/enterprise-systems/${systemId}`);
    },
    onError: (error: any) => {
      toast.error(`Failed to update system: ${error.message}`);
    },
  });

  /**
   * Autocomplete: Search systems library
   */
  const handleSystemSearch = useCallback(async (query: string) => {
    try {
      const response = await searchSystemsLibrary(query, 10);
      return response.results.map((system) => ({
        value: system.system_name,
        label: system.system_name,
        sublabel: `${system.vendor} • ${system.category}${system.version ? ` • ${system.version}` : ''}`,
        data: system,
      }));
    } catch (error) {
      console.error('System search error:', error);
      return [];
    }
  }, []);

  /**
   * Autocomplete: Handle system selection
   */
  const handleSystemSelect = useCallback(async (option: any) => {
    const selectedSystem: LibrarySystem = option.data;

    // Update form with selected system data
    setFormData((prev) => ({
      ...prev,
      system_name: selectedSystem.system_name,
      vendor: selectedSystem.vendor,
      category: selectedSystem.category || prev.category,
      version: selectedSystem.version || '',
      database_type: selectedSystem.database_type || '',
    }));

    toast.success(`Auto-filled from library: ${selectedSystem.system_name}`);
  }, []);

  /**
   * Autocomplete: Search vendors
   */
  const handleVendorSearch = useCallback(async (query: string) => {
    try {
      const response = await searchVendors(query, 10);
      return response.vendors.map((vendor) => ({
        value: vendor,
        label: vendor,
        sublabel: 'Vendor',
        data: { vendor },
      }));
    } catch (error) {
      console.error('Vendor search error:', error);
      return [];
    }
  }, []);

  /**
   * Autocomplete: Handle vendor selection
   */
  const handleVendorSelect = useCallback((option: any) => {
    setFormData((prev) => ({
      ...prev,
      vendor: option.value,
    }));
  }, []);

  /**
   * LLM: Suggest vendor based on system name
   */
  const handleVendorSuggestion = useCallback(async (systemName: string) => {
    if (!systemName || systemName.length < 3) return;

    setIsLoadingVendorSuggestion(true);
    try {
      const suggestion = await suggestVendor(systemName);

      if (suggestion.suggested_vendor && suggestion.confidence !== 'low') {
        setFormData((prev) => ({
          ...prev,
          vendor: suggestion.suggested_vendor,
          category: suggestion.category || prev.category,
          version: suggestion.version || prev.version,
        }));

        const confidenceIcon = suggestion.confidence === 'high' ? '🎯' : '💡';
        toast.success(
          `${confidenceIcon} Auto-suggested vendor: ${suggestion.suggested_vendor} (${suggestion.confidence} confidence)`,
          { duration: 4000 }
        );
      }
    } catch (error) {
      console.error('Vendor suggestion error:', error);
      // Silent failure - vendor suggestion is optional
    } finally {
      setIsLoadingVendorSuggestion(false);
    }
  }, []);

  /**
   * Handle system name change
   */
  const handleSystemNameChange = useCallback(
    (value: string) => {
      setFormData((prev) => ({ ...prev, system_name: value }));

      // Trigger vendor suggestion after short delay
      if (value.length >= 3 && !formData.vendor) {
        const timer = setTimeout(() => {
          handleVendorSuggestion(value);
        }, 500);
        return () => clearTimeout(timer);
      }
    },
    [formData.vendor, handleVendorSuggestion]
  );

  /**
   * Handle vendor change
   */
  const handleVendorChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, vendor: value }));
  }, []);

  /**
   * Handle input change (for non-autocomplete fields)
   */
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  /**
   * Handle form submit
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.system_name.trim()) {
      toast.error('System name is required');
      return;
    }

    if (!formData.vendor.trim()) {
      toast.error('Vendor is required');
      return;
    }

    // Validate custom category name when OTHER is selected
    if (formData.category === 'OTHER' && !formData.custom_category_name.trim()) {
      toast.error('Custom category name is required when selecting "Other"');
      return;
    }

    // Submit - use update mutation if in edit mode, create otherwise
    if (isEditMode) {
      updateMutation.mutate({
        system_name: formData.system_name,
        vendor: formData.vendor,
        category: formData.category,
        custom_category_name: formData.category === 'OTHER' ? formData.custom_category_name : undefined,
        version: formData.version,
        database_type: formData.database_type,
        product_url: formData.product_url || undefined,
      });
    } else {
      createMutation.mutate({
        ...formData,
        custom_category_name: formData.category === 'OTHER' ? formData.custom_category_name : undefined,
        product_url: formData.product_url || undefined,
      });
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    if (isEditMode) {
      navigate(`/enterprise-systems/${systemId}`);
    } else {
      navigate('/enterprise-systems');
    }
  };

  // Show loading state while fetching system data in edit mode
  if (isEditMode && isLoadingSystem) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-neutral-600">Loading system details...</p>
        </div>
      </div>
    );
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              aria-label="Back to list"
            >
              <ArrowLeft className="w-5 h-5 text-neutral-600" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary-50 rounded-lg">
                <Server className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-neutral-900">
                  {isEditMode ? 'Edit Enterprise System' : 'Create Enterprise System'}
                </h1>
                <p className="text-sm text-neutral-600 mt-1">
                  {isEditMode
                    ? 'Update system information and settings'
                    : 'Add a new enterprise system to the catalog'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-neutral-200 p-8">
          {/* System Name - Autocomplete */}
          <div className="mb-6">
            <Autocomplete
              label="System Name"
              placeholder="Start typing to search... (e.g., SAP, Oracle, Dynamics)"
              value={formData.system_name}
              onValueChange={handleSystemNameChange}
              onSelect={handleSystemSelect}
              onSearch={handleSystemSearch}
              minSearchLength={3}
              debounceMs={300}
              helperText="Search from 30 pre-researched enterprise systems or enter custom name"
              required
            />
          </div>

          {/* Vendor - Autocomplete with LLM Suggestion */}
          <div className="mb-6">
            <Autocomplete
              label="Vendor"
              placeholder="Start typing to search... (e.g., SAP, Microsoft)"
              value={formData.vendor}
              onValueChange={handleVendorChange}
              onSelect={handleVendorSelect}
              onSearch={handleVendorSearch}
              minSearchLength={2}
              debounceMs={300}
              helperText={
                isLoadingVendorSuggestion
                  ? 'AI is suggesting vendor...'
                  : 'Company that develops/maintains the system (auto-suggested by AI)'
              }
              required
            />
            {isLoadingVendorSuggestion && (
              <div className="mt-2 flex items-center gap-2 text-sm text-secondary-600">
                <Sparkles className="w-4 h-4 animate-pulse" />
                <span>AI is analyzing system name to suggest vendor...</span>
              </div>
            )}
          </div>

          {/* Category - Combobox style with suggestions and custom input */}
          <div className="mb-6">
            <label
              htmlFor="category_input"
              className="block text-sm font-medium text-neutral-700 mb-2"
            >
              Category
            </label>
            <div className="relative">
              <input
                type="text"
                id="category_input"
                list="category-suggestions"
                value={
                  formData.category === 'OTHER' && formData.custom_category_name
                    ? formData.custom_category_name
                    : formData.category === 'ERP' ? 'ERP (Enterprise Resource Planning)'
                    : formData.category === 'CRM' ? 'CRM (Customer Relationship Management)'
                    : formData.category === 'SCM' ? 'SCM (Supply Chain Management)'
                    : formData.category === 'HCM' ? 'HCM (Human Capital Management)'
                    : formData.category === 'FIN' ? 'FIN (Financial Management)'
                    : formData.category === 'PLM' ? 'PLM (Product Lifecycle Management)'
                    : formData.category === 'BI' ? 'BI (Business Intelligence)'
                    : formData.category === 'MES' ? 'MES (Manufacturing Execution System)'
                    : formData.category === 'WMS' ? 'WMS (Warehouse Management System)'
                    : formData.category
                }
                onChange={(e) => {
                  const inputValue = e.target.value;
                  // Map input to category enum
                  const categoryMap: Record<string, string> = {
                    'ERP (Enterprise Resource Planning)': 'ERP',
                    'CRM (Customer Relationship Management)': 'CRM',
                    'SCM (Supply Chain Management)': 'SCM',
                    'HCM (Human Capital Management)': 'HCM',
                    'FIN (Financial Management)': 'FIN',
                    'PLM (Product Lifecycle Management)': 'PLM',
                    'BI (Business Intelligence)': 'BI',
                    'MES (Manufacturing Execution System)': 'MES',
                    'WMS (Warehouse Management System)': 'WMS',
                  };

                  const matchedCategory = categoryMap[inputValue];
                  if (matchedCategory) {
                    setFormData((prev) => ({
                      ...prev,
                      category: matchedCategory,
                      custom_category_name: '',
                    }));
                  } else {
                    // Custom category - set to OTHER and store the name
                    setFormData((prev) => ({
                      ...prev,
                      category: 'OTHER',
                      custom_category_name: inputValue,
                    }));
                  }
                }}
                placeholder="Type to search or enter custom category..."
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <datalist id="category-suggestions">
                <option value="ERP (Enterprise Resource Planning)" />
                <option value="CRM (Customer Relationship Management)" />
                <option value="SCM (Supply Chain Management)" />
                <option value="HCM (Human Capital Management)" />
                <option value="FIN (Financial Management)" />
                <option value="PLM (Product Lifecycle Management)" />
                <option value="BI (Business Intelligence)" />
                <option value="MES (Manufacturing Execution System)" />
                <option value="WMS (Warehouse Management System)" />
              </datalist>
            </div>
            <p className="text-xs text-neutral-500 mt-1">
              Select from standard categories or type a custom category name
            </p>
            {formData.category === 'OTHER' && formData.custom_category_name && (
              <p className="text-xs text-secondary-600 mt-1">
                Custom category: {formData.custom_category_name}
              </p>
            )}
          </div>

          {/* Version */}
          <div className="mb-6">
            <label
              htmlFor="version"
              className="block text-sm font-medium text-neutral-700 mb-2"
            >
              Version (Optional)
            </label>
            <input
              type="text"
              id="version"
              name="version"
              value={formData.version}
              onChange={handleInputChange}
              placeholder="e.g., 2023, R3, Cloud"
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Version or release of the system
            </p>
          </div>

          {/* Database Type */}
          <div className="mb-6">
            <label
              htmlFor="database_type"
              className="block text-sm font-medium text-neutral-700 mb-2"
            >
              Database Type (Optional)
            </label>
            <input
              type="text"
              id="database_type"
              name="database_type"
              value={formData.database_type}
              onChange={handleInputChange}
              placeholder="e.g., HANA, Oracle, SQL Server, PostgreSQL"
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Underlying database technology
            </p>
          </div>

          {/* Product Website URL */}
          <div className="mb-6">
            <label
              htmlFor="product_url"
              className="block text-sm font-medium text-neutral-700 mb-2"
            >
              Product Website URL (Optional)
            </label>
            <input
              type="url"
              id="product_url"
              name="product_url"
              value={formData.product_url}
              onChange={handleInputChange}
              placeholder="e.g., https://www.sap.com/products/erp.html"
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Official product website - our AI will use this for deeper research
            </p>
          </div>

          {/* Trigger Research - Only show in create mode */}
          {!isEditMode && (
            <div className="mb-8 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="trigger_research"
                  checked={formData.trigger_research}
                  onChange={handleInputChange}
                  className="mt-1 w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                />
                <div className="flex-1">
                  <span className="block text-sm font-medium text-neutral-900">
                    Trigger AI Research After Creation
                  </span>
                  <span className="block text-xs text-neutral-600 mt-1">
                    Automatically start deep research to extract modules, schema, APIs,
                    and documentation (can take 5-15 minutes)
                  </span>
                </div>
              </label>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-neutral-200">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors"
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Server className="w-4 h-4" />
                  {isEditMode ? 'Update System' : 'Create System'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEnterpriseSystemPage;
